//constants
import { SearchableSelectType } from '../components/inputs/searchableSelect';

import { DefaultCampaignId } from '../constants/PushProvider';
import { messageProvidersPath } from '../constants/aampeDocsUrls';
//models
import { FormulaType } from '../constants/formulaType';

export interface PushIntegrationModel {
    id: string;
    push_provider_name: PushProviders;
    customer_id: number;
    field_map: { [key: string]: string };
    status: PushIntegrationStatus;
    created_at: string;
    updated_at: string;
    channels?: FormulaType[];
}

export enum PushIntegrationStatus {
    Pending = 'pending',
    Active = 'active',
}

export enum ProviderActionState {
    Pending = 'Pending',
    Connected = 'Connected',
    Connect = 'Connect',
}

export enum PushProviders {
    Braze = 'Braze',
    CleverTap = 'CleverTap',
    Firebase = 'Firebase',
    Moengage = 'Moengage',
    OneSignal = 'OneSignal',
    Salesforce = 'Salesforce',
    Airship = 'Airship',
    APNS = 'Apple Push Notification service (APNs)',
    Iterable = 'Iterable',
    Netcore = 'Netcore',
    WebEngage = 'WebEngage',
    Emarsys = 'Emarsys',
    Leanplum = 'Leanplum',
    CustomerIO = 'customer.io',
    Twilio = 'Twilio',
    MailChimpMandrill = 'MailChimpMandrill',
    Custom = 'Custom',
}

export enum PushProviderDocsSlug {
    Braze = 'braze',
    CleverTap = 'clevertap',
    Firebase = 'firebase-cloud',
    Moengage = 'moengage',
    OneSignal = 'onesignal',
    Salesforce = 'sfmc',
    Airship = 'airship',
    APNS = 'apns',
    Iterable = 'iterable',
    Netcore = 'netcore',
    WebEngage = 'webengage',
    Emarsys = 'emarsys',
    Leanplum = 'leanplum',
    CustomerIO = 'customerio',
    Twilio = 'twilio',
    MailChimpMandrill = 'mandrill',
}

export enum ConditionOperator {
    Includes = 'includes',
    NotIncludes = 'not_includes',
}

export interface PushProviderFieldModel {
    key: string;
    label: string;
    type: 'text' | 'password' | 'file' | 'textArea' | 'dropdown';
    placeholder?: string;
    is_credential?: boolean;
    is_optional?: boolean;
    default_value?: string;
    description?: string;
    is_copy?: boolean;
    options?: SearchableSelectType[] | null;
    conditions?: {
        dependsOn: 'selectedChannels';
        operator: ConditionOperator;
        value: FormulaType;
    }[];
}

export interface PushProviderDataModel {
    name: string;
    logo: string;
    fields: PushProviderFieldModel[];
    link: string;
    supportedChannels: FormulaType[];
    usedChannels?: FormulaType[];
    status?: string;
    field_map?: { [key: string]: string };
    docsName?: string;
}

export const pushProvidersData: {
    [key in PushProviders]: PushProviderDataModel;
} = {
    [PushProviders.Airship]: {
        name: 'Airship',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/airship.svg',
        fields: [
            {
                key: 'app_key',
                label: 'App Key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'access_token',
                label: 'Access Token',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Airship,
        link: messageProvidersPath + PushProviderDocsSlug.Airship,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.Braze]: {
        name: 'Braze',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/braze.svg',
        fields: [
            {
                key: 'api_key',
                label: 'API Key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'dashboard_url',
                label: 'Dashboard URL',
                type: 'text',
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
            {
                key: 'app_id',
                label: 'App Id',
                type: 'text',
                is_optional: true,
                is_credential: true,
                conditions: [
                    {
                        dependsOn: 'selectedChannels',
                        operator: ConditionOperator.Includes,
                        value: FormulaType.email,
                    },
                ],
            },
        ],
        docsName: PushProviderDocsSlug.Braze,
        link: messageProvidersPath + PushProviderDocsSlug.Braze,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },

    [PushProviders.CleverTap]: {
        name: 'CleverTap',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/clevertap.svg',
        fields: [
            {
                key: 'account_id',
                label: 'Account ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'passcode',
                label: 'Passcode',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.CleverTap,
        link: messageProvidersPath + PushProviderDocsSlug.CleverTap,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.Firebase]: {
        name: 'Firebase',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/firebase.svg',
        fields: [
            {
                key: 'firebase_app_name',
                label: 'Firebase App Name',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'json_credentials',
                label: 'JSON credentials',
                type: 'textArea',
                placeholder:
                    'Paste the content of your JSON credentials file here',
            },
        ],
        docsName: PushProviderDocsSlug.Firebase,
        link: messageProvidersPath + PushProviderDocsSlug.Firebase,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
        ],
    },
    [PushProviders.Iterable]: {
        name: 'Iterable',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/iterable.svg',
        fields: [
            {
                key: 'api_key',
                label: 'API Key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Iterable,
        link: messageProvidersPath + PushProviderDocsSlug.Iterable,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.Moengage]: {
        name: 'Moengage',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/moengage.svg',
        fields: [
            {
                key: 'api_id',
                label: 'API ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'api_secret',
                label: 'API secret',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Moengage,
        link: messageProvidersPath + PushProviderDocsSlug.Moengage,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.OneSignal]: {
        name: 'OneSignal',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/one_signal.svg',
        fields: [
            {
                key: 'app_id',
                label: 'OneSignal App ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'api_key',
                label: 'Rest API Key',
                type: 'text',
                is_credential: true,
            },
        ],
        docsName: PushProviderDocsSlug.OneSignal,
        link: messageProvidersPath + PushProviderDocsSlug.OneSignal,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
        ],
    },
    [PushProviders.Salesforce]: {
        name: 'Salesforce',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/salesforce.svg',
        fields: [
            {
                key: 'client_id',
                label: 'Client ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'client_secret',
                label: 'Client Secret',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'subdomain',
                label: 'Subdomain',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Salesforce,
        link: messageProvidersPath + PushProviderDocsSlug.Salesforce,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.APNS]: {
        name: 'Apple Push Notification service (APNs)',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/apns.svg',
        fields: [
            {
                key: 'encryption_key',
                label: 'Encryption key',
                type: 'textArea',
                placeholder: 'Paste the contents of your .P8 file here',
            },
            {
                key: 'key_id',
                label: 'Key ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'team_id',
                label: 'Team ID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'bundle_id',
                label: 'Bundle ID',
                type: 'text',
                is_credential: true,
            },
        ],
        docsName: PushProviderDocsSlug.APNS,
        link: messageProvidersPath + PushProviderDocsSlug.APNS,
        supportedChannels: [FormulaType.mobilePush],
    },
    [PushProviders.Netcore]: {
        name: 'Netcore',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/netcore.svg',
        fields: [
            {
                key: 'api_key',
                label: 'API Key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Netcore,
        link: messageProvidersPath + PushProviderDocsSlug.Netcore,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.WebEngage]: {
        name: 'WebEngage',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/webengage.svg',
        fields: [
            {
                key: 'license_code',
                label: 'License Code',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'api_key',
                label: 'API Key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'dashboard_url',
                label: 'Dashboard URL',
                type: 'text',
            },
        ],
        docsName: PushProviderDocsSlug.WebEngage,
        link: messageProvidersPath + PushProviderDocsSlug.WebEngage,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.Emarsys]: {
        name: 'Emarsys',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/emarsys.svg',
        fields: [
            {
                key: 'api_username',
                label: 'API User name',
                type: 'text',
            },
            {
                key: 'api_secret',
                label: 'API Secret',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'external_event_id',
                label: 'External Event ID',
                type: 'text',
                is_credential: true,
            },
        ],
        docsName: PushProviderDocsSlug.Emarsys,
        link: messageProvidersPath + PushProviderDocsSlug.Emarsys,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.sms,
            FormulaType.email,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.Leanplum]: {
        name: 'Leanplum',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/leanpulm.svg',
        fields: [
            {
                key: 'api_id',
                label: 'API Id',
                type: 'text',
            },
            {
                key: 'production_api',
                label: 'Production API',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'data_export_api',
                label: 'Data Export API',
                type: 'text',
                is_credential: true,
                is_optional: true,
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.Leanplum,
        link: messageProvidersPath + PushProviderDocsSlug.Leanplum,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.sms,
            FormulaType.email,
            FormulaType.whatsapp,
        ],
    },
    [PushProviders.CustomerIO]: {
        name: 'customer.io',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/customerio.svg',
        fields: [
            {
                key: 'api_key',
                label: 'API key',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'region',
                label: 'Region',
                type: 'text',
            },
            {
                key: 'campaign_id',
                label: 'Campaign Id',
                type: 'text',
                is_optional: true,
                default_value: DefaultCampaignId,
            },
        ],
        docsName: PushProviderDocsSlug.CustomerIO,
        link: messageProvidersPath + PushProviderDocsSlug.CustomerIO,
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
        ],
    },
    [PushProviders.Twilio]: {
        name: 'Twilio',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/twilio.svg',
        fields: [
            {
                key: 'account_sid',
                label: 'Account SID',
                type: 'text',
                is_credential: true,
            },
            {
                key: 'auth_token',
                label: 'Auth Token',
                type: 'text',
                is_credential: true,
            },
        ],
        docsName: PushProviderDocsSlug.Twilio,
        link: messageProvidersPath + PushProviderDocsSlug.Twilio,
        supportedChannels: [FormulaType.mobilePush, FormulaType.sms],
    },
    [PushProviders.MailChimpMandrill]: {
        name: 'Mailchimp transactional email (Mandrill)',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/mailchimp.svg',
        fields: [
            {
                key: 'api_key',
                label: 'API Key',
                type: 'password',
                is_credential: true,
            },
        ],
        docsName: PushProviderDocsSlug.MailChimpMandrill,
        link: messageProvidersPath + PushProviderDocsSlug.MailChimpMandrill,
        supportedChannels: [FormulaType.email],
    },
    [PushProviders.Custom]: {
        name: 'Custom',
        logo: 'https://storage.googleapis.com/assets.aampe.com/app_images/providers_logos/custom.svg',
        fields: [],
        link: messageProvidersPath + 'custom-api',
        supportedChannels: [
            FormulaType.mobilePush,
            FormulaType.webPush,
            FormulaType.email,
            FormulaType.sms,
            FormulaType.whatsapp,
        ],
    },
};

export interface InitialValueType {
    selectedChannels: FormulaType[];
    fields: { [key: string]: string };
}

export enum MarkdownType {
    Table = 'table',
    Header = 'header',
    Image = 'image',
    BlockQuote = 'blockQuote',
}
export interface ExtractedBlockContent {
    type: MarkdownType;
    data?: {
        [key: string]: string;
    };
    h2?: string;
    src?: string;
    text?: string;
    title?: string;
    body?: string;
}

export interface ProviderReadMeDoc {
    body: string;
}

export interface PushIntegrationUpdateModel {
    field_map: Record<string, string>;
    channels: FormulaType[];
}
